import { Link } from '@tanstack/react-router'
import { AlertTriangle, Ban, Check, ChevronDown, ChevronUp, Copy, ExternalLink, Loader2, RotateCw, Upload, X } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Meter } from '@/components/ui/progress'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { formatBytes } from '@/lib/format'
import { cn } from '@/lib/utils'
import { ACTIVE_PHASES, FINISHED_PHASES, type UploadTask } from '../upload/manager'
import { overallProgress, uploadManager, useUploads } from '../upload/store'
import { KindTile } from './media-visuals'

/** Phase → short status line for a task. */
export function taskStatusText(t: UploadTask): string {
  switch (t.phase) {
    case 'queued':
      return 'Waiting…'
    case 'hashing':
      return t.hashProgress > 0 ? `Checking for duplicates · ${Math.round(t.hashProgress * 100)}%` : 'Preparing…'
    case 'starting':
      return 'Starting upload…'
    case 'uploading':
      return `${formatBytes(t.uploadedBytes)} of ${formatBytes(t.size)} · ${Math.floor(t.progress * 100)}%`
    case 'completing':
      return 'Finishing upload…'
    case 'processing':
      return 'Processing…'
    case 'ready':
      return t.replaceOf ? 'Replaced · new revision ready' : `Ready · ${formatBytes(t.size)}`
    case 'duplicate':
      return 'Already in library'
    case 'rejected':
    case 'failed':
      return t.error?.message ?? 'Upload failed'
    case 'canceled':
      return 'Canceled'
  }
}

/** Warn before closing the tab while bytes are still being sent. */
export function useUnloadGuard() {
  const active = useUploads((s) => s.tasks.some((t) => ACTIVE_PHASES.has(t.phase)))
  useEffect(() => {
    if (!active) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [active])
}

/** Floating bottom-right panel listing uploads (mounted once in the app shell). */
export function UploadTray() {
  const tasks = useUploads((s) => s.tasks)
  const collapsed = useUploads((s) => s.collapsed)
  const setCollapsed = useUploads((s) => s.setCollapsed)
  const ref = useRef<HTMLElement>(null)
  useUnloadGuard()
  const visible = tasks.length > 0
  useEffect(() => {
    const el = ref.current
    if (!visible || !el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => useUploads.setState({ trayHeight: el.offsetHeight }))
    ro.observe(el)
    return () => {
      ro.disconnect()
      useUploads.setState({ trayHeight: 0 })
    }
  }, [visible])
  if (!visible) return null

  const active = tasks.filter((t) => ACTIVE_PHASES.has(t.phase)).length
  const processing = tasks.filter((t) => t.phase === 'processing').length
  const failed = tasks.filter((t) => t.phase === 'failed' || t.phase === 'rejected').length
  const finished = tasks.filter((t) => FINISHED_PHASES.has(t.phase)).length
  const overall = overallProgress(tasks)
  const title =
    active > 0 ? `Uploading ${active} file${active === 1 ? '' : 's'}` : processing > 0 ? `Processing ${processing} file${processing === 1 ? '' : 's'}` : failed > 0 ? `${failed} upload${failed === 1 ? '' : 's'} need attention` : 'Uploads complete'

  return (
    <section
      ref={ref}
      aria-label="Uploads"
      className="fixed right-4 bottom-4 z-40 w-[min(380px,calc(100vw-2rem))] overflow-hidden rounded-xl border bg-popover text-popover-foreground shadow-lg animate-in fade-in-0 slide-in-from-bottom-4"
    >
      <header className="flex items-center gap-2 border-b px-3 py-2.5">
        <span className={cn('flex size-7 items-center justify-center rounded-lg', active || processing ? 'bg-primary/10 text-primary' : failed ? 'bg-danger/10 text-danger' : 'bg-success/12 text-success')}>
          {active || processing ? <Upload className="size-3.5" /> : failed ? <AlertTriangle className="size-3.5" /> : <Check className="size-3.5" strokeWidth={3} />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-semibold" aria-live="polite">
            {title}
          </p>
          {overall !== null && <Meter value={overall} tone="brand" size="sm" className="mt-1" label="Overall upload progress" />}
        </div>
        {finished > 0 && !collapsed && (
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => uploadManager.clearFinished()}>
            Clear
          </Button>
        )}
        <Button variant="ghost" size="icon-sm" className="size-7" aria-label={collapsed ? 'Expand uploads' : 'Collapse uploads'} aria-expanded={!collapsed} onClick={() => setCollapsed(!collapsed)}>
          {collapsed ? <ChevronUp /> : <ChevronDown />}
        </Button>
        {active === 0 && processing === 0 && (
          <Button variant="ghost" size="icon-sm" className="size-7" aria-label="Close uploads" onClick={() => uploadManager.clearFinished()}>
            <X />
          </Button>
        )}
      </header>
      {!collapsed && (
        <ul className="max-h-[min(360px,50vh)] divide-y overflow-y-auto scrollbar-thin">
          {[...tasks].reverse().map((t) => (
            <UploadRow key={t.id} task={t} />
          ))}
        </ul>
      )}
    </section>
  )
}

export function UploadRow({ task: t, dense = false }: { task: UploadTask; dense?: boolean }) {
  const failed = t.phase === 'failed' || t.phase === 'rejected'
  const duplicate = t.phase === 'duplicate' || (t.phase === 'rejected' && !!t.duplicateOfId)
  const existingId = t.duplicateOf?.id ?? t.duplicateOfId
  const busy = ACTIVE_PHASES.has(t.phase) || t.phase === 'processing'
  const progress = t.phase === 'hashing' ? null : t.phase === 'uploading' ? t.progress : null
  return (
    <li className={cn('flex items-start gap-3 px-3', dense ? 'py-2' : 'py-2.5')}>
      <div className="relative">
        {t.kind ? <KindTile kind={t.kind} size="sm" /> : <span className="flex size-7 items-center justify-center rounded-lg bg-muted text-muted-foreground">?</span>}
        <span className="absolute -right-1 -bottom-1 flex size-3.5 items-center justify-center rounded-full bg-popover">
          {t.phase === 'ready' ? (
            <span className="flex size-3 items-center justify-center rounded-full bg-success text-white">
              <Check className="size-2" strokeWidth={4} />
            </span>
          ) : duplicate ? (
            <Copy className="size-3 text-info" />
          ) : failed ? (
            <span className="flex size-3 items-center justify-center rounded-full bg-danger text-white">
              <X className="size-2" strokeWidth={4} />
            </span>
          ) : t.phase === 'canceled' ? (
            <Ban className="size-3 text-muted-foreground" />
          ) : (
            <Loader2 className="size-3 animate-spin text-primary" />
          )}
        </span>
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-medium" title={t.name}>
          {t.name}
        </p>
        <p className={cn('truncate text-xs', failed && !duplicate ? 'text-danger' : 'text-muted-foreground')} title={taskStatusText(t)}>
          {taskStatusText(t)}
        </p>
        {(t.phase === 'uploading' || t.phase === 'hashing' || t.phase === 'completing' || t.phase === 'starting') && (
          <Meter value={progress ?? (t.phase === 'hashing' ? t.hashProgress : t.phase === 'completing' ? 1 : 0)} size="sm" tone={t.phase === 'hashing' ? 'warning' : 'ok'} className="mt-1.5" label={`Progress for ${t.name}`} />
        )}
        {duplicate && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {existingId && (
              <Button variant="outline" size="sm" className="h-6 px-2 text-xs" asChild>
                <Link to="/media" search={{ media: existingId }}>
                  <ExternalLink className="size-3" /> View existing
                </Link>
              </Button>
            )}
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => uploadManager.uploadAnyway(t.id)}>
              Upload anyway
            </Button>
          </div>
        )}
        {t.phase === 'ready' && t.mediaId && !dense && (
          <Link to="/media" search={{ media: t.mediaId }} className="mt-0.5 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
            Open <ExternalLink className="size-3" />
          </Link>
        )}
      </div>
      <div className="flex shrink-0 items-center">
        {(failed && !duplicate) || t.phase === 'canceled' ? (
          <TrayIcon label="Retry" onClick={() => uploadManager.retry(t.id)}>
            <RotateCw />
          </TrayIcon>
        ) : null}
        {busy && t.phase !== 'processing' ? (
          <TrayIcon label="Cancel upload" onClick={() => uploadManager.cancel(t.id)}>
            <X />
          </TrayIcon>
        ) : !busy ? (
          <TrayIcon label="Dismiss" onClick={() => uploadManager.dismiss(t.id)}>
            <X />
          </TrayIcon>
        ) : (
          <span className="px-1.5 text-[11px] text-muted-foreground tabular">{formatBytes(t.size)}</span>
        )}
      </div>
    </li>
  )
}

function TrayIcon({ label, onClick, children }: { label: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button variant="ghost" size="icon-sm" className="size-7 text-muted-foreground" aria-label={label} onClick={onClick}>
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  )
}
